/**
 * Serializer for teaching-plan markdown. The agent (or its mock) still
 * returns the legacy 4-section ExtensionMaterials shape — we just
 * re-frame the output as a Markdown teaching plan that lands in the
 * teacher's /documents/teaching-plans/ folder.
 *
 * Keeping serialization client-side avoids a school-backend round-trip
 * and means the same JSON-to-MD mapping is the source of truth for
 * both "generate + save" and any future re-render (e.g. preview pane).
 * If JZ ships a new lesson-plan agent with a different schema later,
 * we extend this with a second emitter; the file's filename convention
 * stays the same.
 */
import type { ExtensionMaterials, TeachingPlan } from "../../api/school";

export interface PlanFilenameInput {
  week: number;
  topic: string;
}

/** Filename convention for plans on disk:
 *   week-03_amazing-things.md
 *
 * Week is zero-padded so the lexical sort matches the chronological
 * order in NotesFilesPage (where rows are alphabetical). Topic is
 * kebab-cased + ASCII-only, with a length cap so Nova OS's filesystem
 * + the i18n display column don't get unwieldy.
 */
export function planFilename({ week, topic }: PlanFilenameInput): string {
  const weekTwo = Math.max(0, Math.floor(week)).toString().padStart(2, "0");
  const slug = topic
    .toLowerCase()
    .replace(/[^a-z0-9\s_-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 40)
    || "plan";
  return `week-${weekTwo}_${slug}.md`;
}

/** Parse a filename back into its metadata. Used by the list view to
 *  surface week + topic columns without having to fetch each plan's
 *  body. Tolerates anything that didn't come from planFilename — we
 *  just return what we can. */
export function parsePlanFilename(name: string): {
  week: number | null;
  topicSlug: string;
} {
  const m = name.match(/^week-(\d+)_(.+?)(?:\.md)?$/i);
  if (!m) return { week: null, topicSlug: name.replace(/\.md$/i, "") };
  const week = Number.parseInt(m[1] ?? "", 10);
  return {
    week: Number.isFinite(week) ? week : null,
    topicSlug: m[2] ?? "",
  };
}

interface SerializeOpts {
  week: number;
  topic: string;
  className?: string | null;
  customPrompt?: string | null;
  generatedAt: Date;
}

export function serializeTeachingPlan(
  m: ExtensionMaterials,
  opts: SerializeOpts,
): string {
  const lines: string[] = [];
  lines.push(`# Week ${opts.week} — ${opts.topic}`);
  lines.push("");
  lines.push(`> Generated ${opts.generatedAt.toLocaleString()}`);
  if (opts.className) lines.push(`> Class: ${opts.className}`);
  if (opts.customPrompt) lines.push(`> Teacher instructions: ${opts.customPrompt}`);
  lines.push("");

  // ── Reading ──────────────────────────────────────────────────────
  const r = m.extension_reading;
  lines.push(`## 1. Reading — ${r.title}`);
  lines.push("");
  lines.push(`*${r.word_count} words · difficulty ${r.difficulty}*`);
  lines.push("");
  lines.push(r.text);
  lines.push("");
  if (r.vocabulary_notes && r.vocabulary_notes.length > 0) {
    lines.push("### Vocabulary notes");
    lines.push("");
    for (const v of r.vocabulary_notes) {
      lines.push(`- **${v.word}** — ${v.meaning_zh}: *${v.example}*`);
    }
    lines.push("");
  }
  if (r.comprehension_questions && r.comprehension_questions.length > 0) {
    lines.push("### Comprehension questions");
    lines.push("");
    r.comprehension_questions.forEach((q, i) => {
      lines.push(`${i + 1}. ${q.question}`);
      lines.push(`   *Answer: ${q.answer}*`);
    });
    lines.push("");
  }

  // ── Practice exercises ────────────────────────────────────────────
  if (m.practice_exercises.length > 0) {
    lines.push(`## 2. Practice exercises (${m.practice_exercises.length})`);
    lines.push("");
    for (const ex of m.practice_exercises) {
      lines.push(
        `### Q${ex.id} — ${ex.type} · ${ex.target_skill} · level ${ex.level}`,
      );
      lines.push("");
      lines.push(ex.instruction);
      lines.push("");
      lines.push("```");
      lines.push(ex.content);
      lines.push("```");
      lines.push("");
      lines.push(`*Answer: ${ex.answer}*`);
      lines.push("");
    }
  }

  // ── Discussion prompts ────────────────────────────────────────────
  if (m.discussion_prompts.length > 0) {
    lines.push(`## 3. Discussion prompts (${m.discussion_prompts.length})`);
    lines.push("");
    for (const d of m.discussion_prompts) {
      lines.push(
        `- *(${d.suggested_duration_minutes} min · level ${d.level})*`,
      );
      lines.push(`  - ${d.prompt_en}`);
      lines.push(`  - ${d.prompt_zh}`);
      if (d.teacher_notes) lines.push(`  - Notes: ${d.teacher_notes}`);
    }
    lines.push("");
  }

  // ── Differentiated tasks ──────────────────────────────────────────
  lines.push("## 4. Differentiated tasks");
  lines.push("");
  for (const [level, task] of [
    ["A", m.differentiated_tasks.a_level],
    ["B", m.differentiated_tasks.b_level],
  ] as const) {
    lines.push(
      `### ${level}-level — ${task.task_name} *(${task.expected_duration_minutes} min)*`,
    );
    lines.push("");
    lines.push(task.description);
    lines.push("");
    lines.push(`*Output format: ${task.output_format}*`);
    lines.push("");
  }

  return lines.join("\n");
}

/** v2 (2026-09-15): subject-neutral weekly plan → Markdown for teaching-plans/. */
export function serializeTeachingPlanV2(p: TeachingPlan, opts: SerializeOpts): string {
  const L: string[] = [];
  L.push(`# 第 ${opts.week} 周 — ${opts.topic}`);
  L.push("");
  L.push(`> 生成于 ${opts.generatedAt.toLocaleString()}${p.subject_name ? ` · ${p.subject_name}` : ""}`);
  if (opts.className) L.push(`> 班级:${opts.className}`);
  if (opts.customPrompt) L.push(`> 老师要求:${opts.customPrompt}`);
  L.push("");
  L.push("## 1. 教学目标");
  L.push("");
  p.objectives.forEach((o, i) => L.push(`${i + 1}. ${o}`));
  L.push("");
  L.push("## 2. 知识点");
  L.push("");
  L.push(p.knowledge_points.map((k) => `- ${k}`).join("\n"));
  L.push("");
  L.push("## 3. 课时安排");
  L.push("");
  for (const les of p.lessons) {
    L.push(`### 第 ${les.period} 课时 · ${les.focus}`);
    L.push("");
    les.activities.forEach((a) => L.push(`- ${a}`));
    if (les.materials) L.push(`- 材料:${les.materials}`);
    L.push("");
  }
  L.push("## 4. 课堂练习");
  L.push("");
  p.exercises.forEach((e, i) => {
    L.push(`${i + 1}. ${e.stem}`);
    L.push(`   *答案:${e.answer}*`);
  });
  L.push("");
  L.push("## 5. 分层");
  L.push("");
  L.push(`- 基础层:${p.differentiation.basic}`);
  L.push(`- 拓展层:${p.differentiation.advanced}`);
  L.push("");
  L.push("## 6. 作业");
  L.push("");
  L.push(p.homework);
  L.push("");
  L.push("## 7. 评价");
  L.push("");
  L.push(p.assessment);
  if (p.notes) {
    L.push("");
    L.push(`> ${p.notes}`);
  }
  L.push("");
  return L.join("\n");
}

